// Glass board material — custom TSL glass, not MeshPhysicalMaterial
// transmission. We sample the already-rendered viewport with a normal-based
// refraction offset (cheap, composes with the bloom pipeline — verified in
// the step-0 spike) and add a fresnel rim tinted by rink end: blue at the
// home end shading to magenta at the away end, like the reference render.
//
// The wall geometry's UV.x runs 0→1 along the board path starting at the
// +x edge near the home end; the end-to-end tint uses positionLocal.z
// (three z = -simX, home +z) instead so it needs no path bookkeeping.

import { tuning } from "@arcade-hockey/sim";
import {
  clamp,
  mix,
  normalView,
  positionLocal,
  positionViewDirection,
  screenUV,
  uniform,
  vec4,
  viewportSharedTexture,
} from "three/tsl";
import { Color, DoubleSide, MeshBasicNodeMaterial } from "three/webgpu";
import type { BoardsStyle } from "../style/style-schema.js";

function buildUniforms(style: BoardsStyle) {
  return {
    seeThrough: uniform(style.glass.seeThrough),
    refractStrength: uniform(style.glass.refractStrength),
    fresnelPower: uniform(style.glass.fresnelPower),
    rimIntensity: uniform(style.glass.rimIntensity),
    tintStrength: uniform(style.glass.tintStrength),
    leftColor: uniform(new Color(style.trim.leftColor)),
    rightColor: uniform(new Color(style.trim.rightColor)),
  };
}

export type GlassBoardsUniforms = ReturnType<typeof buildUniforms>;

export function applyGlassBoardsStyle(u: GlassBoardsUniforms, s: BoardsStyle): void {
  u.seeThrough.value = s.glass.seeThrough;
  u.refractStrength.value = s.glass.refractStrength;
  u.fresnelPower.value = s.glass.fresnelPower;
  u.rimIntensity.value = s.glass.rimIntensity;
  u.tintStrength.value = s.glass.tintStrength;
  u.leftColor.value.set(s.trim.leftColor);
  u.rightColor.value.set(s.trim.rightColor);
}

export function createGlassBoardsMaterial(style: BoardsStyle): {
  material: MeshBasicNodeMaterial;
  u: GlassBoardsUniforms;
} {
  const u = buildUniforms(style);

  // Side-to-side tint matching the trim rails (left cyan → right magenta).
  const sideT = clamp(positionLocal.x.div(tuning.rink.width).add(0.5), 0, 1);
  const endTint = mix(u.leftColor, u.rightColor, sideT);

  const fres = normalView.dot(positionViewDirection).abs().oneMinus().pow(u.fresnelPower);
  const refractedUv = screenUV.add(normalView.xy.mul(u.refractStrength));
  const behind = viewportSharedTexture(refractedUv);

  const material = new MeshBasicNodeMaterial({
    transparent: true,
    side: DoubleSide,
    depthWrite: false,
  });
  material.colorNode = vec4(
    behind.rgb
      .mul(u.seeThrough)
      .add(endTint.mul(u.tintStrength).mul(0.3))
      .add(endTint.mul(fres).mul(u.rimIntensity)),
    1,
  );
  return { material, u };
}
